import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Events, tracking } from '@lib';
import { IntakeForm, Question } from '@qmd-types/intakeForms';
import { IntakeFormContext } from './IntakeFormContext';

type ProviderProps = {
  children: ReactNode;
  intakeForm: IntakeForm;
};

export function IntakeFormProvider({ children, intakeForm }: ProviderProps) {
  const isIntakeFormComplete = Boolean(intakeForm.completeDateTime);
  const [step, setStep] = useState<number>(1);
  const [showIntakeFormReview, setShowIntakeFormReview] =
    useState<boolean>(isIntakeFormComplete);
  const [editQuestion, setEditQuestion] = useState<Question | null>(null);
  const [intakeFormState, setIntakeFormState] =
    useState<IntakeForm>(intakeForm);

  const currentQuestion = useMemo(() => {
    return intakeFormState?.questions[step - 1];
  }, [intakeFormState, step]);

  const handleEditQuestionResponse = useCallback(
    (question: Question | null) => {
      if (question) {
        setEditQuestion(question);
        setShowIntakeFormReview(false);
      } else {
        setEditQuestion(null);
        setShowIntakeFormReview(true);
      }
    },
    [],
  );

  const handleNextStep = useCallback(async () => {
    const isLastStep = step === intakeFormState?.questions.length;

    if (isLastStep) {
      return handleEditQuestionResponse(null);
    }

    setStep((prevStep) => prevStep + 1);
  }, [handleEditQuestionResponse, intakeFormState, step]);

  const updateAnswer = useCallback(
    (questionId: string, updatedQuestion: Question) => {
      const updatedQuestions = intakeFormState.questions.map((question) => {
        if (question.intakeFormQuestionId === questionId) {
          return updatedQuestion;
        }

        return question;
      });

      const updatedIntakeForm = {
        ...intakeFormState,
        questions: updatedQuestions,
      };

      const segmentPayload = {
        QuestionText: updatedQuestion.question,
        QuestionType: updatedQuestion.type,
      };

      if (editQuestion) {
        tracking.trackEvent(Events.SAVE_INTAKE_RESPONSE, segmentPayload);
      } else {
        tracking.trackEvent(Events.SUBMIT_INTAKE_FORM_QUESTION, segmentPayload);
      }

      setIntakeFormState(updatedIntakeForm);
    },
    [editQuestion, intakeFormState],
  );

  const value = useMemo(
    () => ({
      currentQuestion,
      editQuestion,
      handleEditQuestionResponse,
      handleNextStep,
      intakeForm: intakeFormState,
      isIntakeFormComplete,
      setStep,
      showIntakeFormReview,
      step,
      updateAnswer,
    }),
    [
      currentQuestion,
      editQuestion,
      handleEditQuestionResponse,
      handleNextStep,
      intakeFormState,
      isIntakeFormComplete,
      showIntakeFormReview,
      step,
      updateAnswer,
    ],
  );

  return (
    <IntakeFormContext.Provider value={value}>
      {children}
    </IntakeFormContext.Provider>
  );
}
